"use strict";
/*
 * Membrane React package
 * {@link https://www.npmjs.com/package/@membranehq/react}
 * v0.29.2
 */
'use client';

import { jsx, jsxs, Fragment } from 'react/jsx-runtime';
import { getReferenceCollectionPathForSchema, schemaWithTitle, pickFieldsFromSchema, getDataCollectionCreateFields, UNIFIED_DATA_MODELS } from '@membranehq/sdk';
import { clsx } from 'clsx';
import { useState } from 'react';
import { useComboBox } from '../../context/use-combobox.mjs';
import '../../../DataBuilder/use-data-builder.mjs';
import { useIntegrationAppConnection } from '../../../../contexts/use-integration-app-connection.mjs';
import { useIntegrationAppIntegration } from '../../../../contexts/use-integration-app-integration.mjs';
import { useMembraneClient } from '../../../../contexts/use-membrane-client.mjs';
import 'fast-deep-equal';
import { usePromise } from '../../../../hooks/usePromise.mjs';
import 'fuse';
import '../../shared.mjs';
import '../../options-factories/formula/config-mapping.mjs';
import 'js-convert-case';
import '../../options-factories/root.mjs';
import '../../Badges/EditorTagBadge/NestedBadgeValue.mjs';
import { getLookupCollection } from './getLookupCollection.mjs';
import { useComboBoxDropdownContext } from '../../context/use-combobox-dropdown.mjs';
import '../../context/use-combobox-dropdown-search.mjs';
import '../../context/use-dropdown-stack.mjs';
import '../../context/use-nested-combobox.mjs';
import { makeDataField } from '../../../DataBuilder/index.mjs';
import { makeSubField } from '../../../DataBuilder/data-field.mjs';
import { DataBuilderFormField } from '../../../DataBuilder/DataBuilderFormField.mjs';
import useDataBuilderField from '../../../DataBuilder/use-data-builder-field.mjs';
import { useFormComponents } from '../../../DataBuilder/use-form-components.mjs';
import { DropdownUI } from '../../../DropdownUI/index.mjs';
import { useKeyboardEventSubscribe, EVENT_TYPE } from '../../../../helpers/hooks/keyboard-events.mjs';
import { withSlotScopeClasses } from '../../../../helpers/scoped-slots.mjs';
import { tv } from 'tailwind-variants';
import { Root, Item, ItemControl, ItemText } from '../../../../ui-kit/styled/radio-group.mjs';
import { Skeleton } from '../../../../ui-kit/skeleton.mjs';
import { useTimeout } from 'usehooks-ts';
import { RadioGroupItemHiddenInput } from '@ark-ui/react/radio-group';

function LookupCategoryContent({ onOptionSelect }) {
  const { field } = useDataBuilderField();
  if (!field) {
    return null;
  }
  function handleSelect(value) {
    onOptionSelect?.(value);
  }
  return /* @__PURE__ */ jsx(DataBuilderDrillDownLookup, { field, onChange: handleSelect });
}
const lookupConfigRecipe = tv({
  slots: withSlotScopeClasses("lookup-config", {
    panel: "p-1.5 flex-col gap-4 min-h-16",
    "nothing-found-selector": "flex pl-2 flex-col gap-2.5 [&>span]:font-medium [&_label]:font-medium",
    "nothing-found-form": "flex-col",
    "loading-container": "flex-col gap-2.5 pointer-events-none",
    skeleton: "h-10 min-h-10 rounded-md w-5/6 opacity-10 transition-opacity duration-100"
  })
});
function FieldSkeleton({ delay = 300 }) {
  const [opacity, setOpacity] = useState(0.1);
  const styles = lookupConfigRecipe();
  useTimeout(() => {
    setOpacity(1);
  }, delay);
  return /* @__PURE__ */ jsx(Skeleton, { className: styles.skeleton(), style: { opacity } });
}
function DataBuilderDrillDownLookup({ field, onChange }) {
  const { DataBuilderForm } = useFormComponents();
  const { client } = useMembraneClient();
  const { integrationId } = useIntegrationAppIntegration();
  const { connectionId } = useIntegrationAppConnection();
  const { drillUp } = useComboBoxDropdownContext();
  const { scopedComboboxId } = useComboBox();
  const lookupValue = field.value?.$lookup ?? {};
  const styles = lookupConfigRecipe();
  const [
    { queryFieldsSchema, createFieldsSchema } = {
      queryFieldsSchema: null,
      createFieldsSchema: null
    },
    error,
    state
  ] = usePromise(
    () => getLookupSchemas({ field, client, connectionId, integrationId }),
    [getReferenceCollectionPathForSchema(field.schema)]
  );
  useKeyboardEventSubscribe({
    scopedComboboxId,
    event: EVENT_TYPE.OPTION_LEFT,
    callback: () => {
      drillUp();
    }
  });
  function handleLookupChange(value) {
    onChange?.({
      $lookup: {
        ...lookupValue,
        ...value
      }
    });
  }
  function handleNewRecordChange(value) {
    onChange?.({
      $lookup: {
        ...lookupValue,
        create: {
          fields: value
        }
      }
    });
  }
  const radioButtonLookupValue = lookupValue.createIfNotFound ? "createNew" : "leaveEmpty";
  function handleLookupValueChange({ value }) {
    const createIfNotFound = value === "createNew";
    handleLookupChange({
      createIfNotFound
    });
  }
  function makeNewRecordField() {
    return makeSubField(field, {
      valueLocator: `${field.valueLocator}.$lookup.create.fields`,
      schema: schemaWithTitle(createFieldsSchema, "Fields to Create"),
      isInSchema: true,
      value: lookupValue.create?.fields,
      iconUri: field.iconUri,
      level: 1
    });
  }
  if (error) {
    return /* @__PURE__ */ jsx(DropdownUI.ErrorHeading, { text: `Error: ${error?.toString()}` });
  }
  if (state === "pending") {
    const dataBuilderField2 = makeDataField({
      value: lookupValue.query,
      schema: schemaWithTitle({}, "Query fields"),
      variablesSchema: field.variablesSchema
    });
    return /* @__PURE__ */ jsxs("div", { className: clsx("flex", styles.panel(), styles["loading-container"]()), children: [
      /* @__PURE__ */ jsx(DataBuilderForm, { showNullValueFields: true, field: dataBuilderField2, onChange: () => {
      } }),
      /* @__PURE__ */ jsx(FieldSkeleton, {}),
      /* @__PURE__ */ jsx(FieldSkeleton, { delay: 500 }),
      /* @__PURE__ */ jsx(FieldSkeleton, { delay: 700 }),
      /* @__PURE__ */ jsx(FieldSkeleton, { delay: 900 })
    ] });
  }
  if (!queryFieldsSchema) {
    return /* @__PURE__ */ jsx(DropdownUI.InfoHeading, { text: "This collection does not support lookup." });
  }
  const dataBuilderField = makeDataField({
    value: lookupValue.query,
    schema: schemaWithTitle(queryFieldsSchema, "Query fields"),
    variablesSchema: field.variablesSchema
  });
  return /* @__PURE__ */ jsx("div", { className: "max-h-full overflow-scroll", children: /* @__PURE__ */ jsx(DropdownUI.Panel, { children: /* @__PURE__ */ jsxs("div", { className: clsx("flex", styles.panel()), children: [
    /* @__PURE__ */ jsx(
      DataBuilderForm,
      {
        showNullValueFields: true,
        field: dataBuilderField,
        onChange: (query) => handleLookupChange({ query })
      }
    ),
    /* @__PURE__ */ jsx("hr", { className: "border-0 border-t border-border-default" }),
    /* @__PURE__ */ jsx("div", { className: clsx("flex", styles.panel()), children: createFieldsSchema && /* @__PURE__ */ jsxs(Fragment, { children: [
      /* @__PURE__ */ jsxs("div", { className: styles["nothing-found-selector"](), children: [
        /* @__PURE__ */ jsx("span", { children: "If nothing found" }),
        /* @__PURE__ */ jsxs(
          Root,
          {
            size: "sm",
            value: radioButtonLookupValue,
            onValueChange: handleLookupValueChange,
            className: "gap-2",
            children: [
              /* @__PURE__ */ jsxs(Item, { value: "leaveEmpty", children: [
                /* @__PURE__ */ jsx(ItemControl, {}),
                /* @__PURE__ */ jsx(ItemText, { className: "font-normal", children: "Leave empty" }),
                /* @__PURE__ */ jsx(RadioGroupItemHiddenInput, {})
              ] }),
              /* @__PURE__ */ jsxs(Item, { value: "createNew", children: [
                /* @__PURE__ */ jsx(ItemControl, {}),
                /* @__PURE__ */ jsx(ItemText, { className: "font-normal", children: "Create new" }),
                /* @__PURE__ */ jsx(RadioGroupItemHiddenInput, {})
              ] })
            ]
          }
        )
      ] }),
      lookupValue.createIfNotFound && /* @__PURE__ */ jsx("div", { className: clsx("flex", styles["nothing-found-form"]()), children: /* @__PURE__ */ jsx(DataBuilderFormField, { field: makeNewRecordField(), onChange: handleNewRecordChange }) })
    ] }) })
  ] }) }) });
}
async function getLookupSchemas({
  field,
  client,
  connectionId,
  integrationId
}) {
  const referencePath = getReferenceCollectionPathForSchema(field?.schema);
  const referenceUdm = field.schema?.referenceUdm;
  if (referencePath && field.schema) {
    const collection = await getLookupCollection({
      schema: field.schema,
      client,
      connectionId,
      integrationId
    });
    if (!collection) {
      return {};
    }
    const queryFieldsSchema = pickFieldsFromSchema(
      collection?.fieldsSchema,
      collection?.match?.fields ?? collection?.find?.queryFields
    );
    delete queryFieldsSchema.required;
    const createFieldsSchema = getDataCollectionCreateFields(collection);
    return {
      queryFieldsSchema,
      createFieldsSchema
    };
  } else if (referenceUdm) {
    const udmSpec = UNIFIED_DATA_MODELS[referenceUdm];
    if (udmSpec) {
      return {
        queryFieldsSchema: udmSpec.match?.fields ? pickFieldsFromSchema(udmSpec.fieldsSchema, udmSpec.match.fields) : void 0,
        createFieldsSchema: udmSpec.create?.fields ? pickFieldsFromSchema(udmSpec.fieldsSchema, udmSpec.create.fields) : udmSpec.fieldsSchema
      };
    } else {
      return {};
    }
  } else {
    return {};
  }
}

export { LookupCategoryContent };
